import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility,
  Phone,
  Email,
  WhatsApp,
  Person,
  ConfirmationNumber,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import bookingService from '../../services/api/bookingService';
import { BookingStatusChip } from '../../utils/bookingStatus';
import { resolveBookingPackageLabel } from '../../utils/bookingPackageLabel';

const FrontDeskSearchPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType] = useState('reference'); // reference, name, phone, email
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }

    setLoading(true);
    setError('');
    setSearched(true);
    setSearchResults([]);

    try {
      let results = [];
      const q = searchQuery.trim();
      const qLower = q.toLowerCase();

      try {
        const byRef = await bookingService.getBookingByReference(q);
        if (byRef) results = [byRef];
      } catch {
        /* fall through to list search */
      }

      if (!results.length) {
        const allBookings = await bookingService.searchBookings({ search: q, limit: 200 });
        results = (allBookings || []).filter((b) => {
          const ref = String(b.bookingReference || '').toLowerCase();
          const travelers = Array.isArray(b.travelerDetails) ? b.travelerDetails : [];
          const travelerNames = travelers.map((t) => `${t.firstName || ''} ${t.lastName || ''}`).join(' ');
          const name = `${b.customerName || ''} ${b.guestFirstName || ''} ${b.guestLastName || ''} ${b.customizedData?.yourName || ''} ${travelerNames}`.toLowerCase();
          const travelerEmails = travelers.map((t) => t.email || '').join(' ');
          const email = `${b.customerEmail || ''} ${b.email || ''} ${b.guestEmail || ''} ${b.customizedData?.email || ''} ${travelerEmails}`.toLowerCase();
          const travelerPhones = travelers.map((t) => `${t.phone || ''} ${t.whatsAppNumber || ''}`).join(' ');
          const phone = `${b.customerPhone || ''} ${b.phoneNumber || ''} ${b.phone || ''} ${b.guestPhone || ''} ${b.customizedData?.phoneNumber || ''} ${travelerPhones}`.replace(/\D/g, '');
          const pkgName = `${b.packageName || ''} ${b.package?.name || ''} ${resolveBookingPackageLabel(b)}`.toLowerCase();
          const qDigits = q.replace(/\D/g, '');
          return (
            ref.includes(qLower) ||
            name.includes(qLower) ||
            email.includes(qLower) ||
            pkgName.includes(qLower) ||
            (qDigits.length >= 3 && phone.includes(qDigits))
          );
        });
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.response?.data?.message || 'Failed to search. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          🔍 Front Desk - Booking Search
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Search for bookings by reference number, customer name, phone, or email
        </Typography>
      </Box>

      {/* Search Box */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Search Booking"
              placeholder="Enter booking reference, name, phone, or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              autoFocus
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </Grid>
        </Grid>

        {/* Quick Search Suggestions */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            💡 Search Tips:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<ConfirmationNumber />}
              label="Booking Reference (BK12345)"
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<Person />}
              label="Customer Name"
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<Phone />}
              label="Phone Number"
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<Email />}
              label="Email Address"
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search Results */}
      {searched && !loading && (
        <>
          {searchResults.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No bookings found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try searching with a different term
              </Typography>
            </Paper>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                Found {searchResults.length} booking{searchResults.length !== 1 ? 's' : ''}
              </Typography>

              <Grid container spacing={3}>
                {searchResults.map((booking) => (
                  <Grid item xs={12} key={booking.id}>
                    <Card>
                      <CardContent>
                        <Grid container spacing={2}>
                          {/* Booking Info */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <ConfirmationNumber color="primary" />
                              <Typography variant="h6">
                                {booking.bookingReference}
                              </Typography>
                              <BookingStatusChip status={booking.status} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Package:</strong> {resolveBookingPackageLabel(booking)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Travel Dates:</strong>{' '}
                              {new Date(booking.travelStartDate).toLocaleDateString()} -{' '}
                              {new Date(booking.travelEndDate).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Travelers:</strong> {booking.numberOfTravelers}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Total Amount:</strong> {booking.currency === 'PKR' ? `PKR ${Number(booking.totalAmount || 0).toLocaleString()}` : `$${booking.totalAmount?.toFixed(2)} ${booking.currency || 'USD'}`}
                            </Typography>
                          </Grid>

                          {/* Customer Info */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                              Customer Information
                            </Typography>
                            {(() => {
                              const t0 = booking.travelerDetails && booking.travelerDetails[0];
                              const custName =
                                (t0 && `${t0.firstName || ''} ${t0.lastName || ''}`.trim()) ||
                                booking.customerName ||
                                (booking.guestFirstName ? `${booking.guestFirstName} ${booking.guestLastName || ''}`.trim() : '') ||
                                booking.customizedData?.yourName ||
                                'Guest Traveler';
                              const custEmail =
                                t0?.email ||
                                booking.customerEmail ||
                                booking.email ||
                                booking.guestEmail ||
                                booking.customizedData?.email;
                              const custPhone =
                                t0?.phone ||
                                booking.customerPhone ||
                                booking.phoneNumber ||
                                booking.phone ||
                                booking.guestPhone ||
                                booking.customizedData?.phoneNumber;
                              const custWA =
                                t0?.whatsAppNumber ||
                                booking.customizedData?.whatsAppNumber;

                              return (
                                <>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Person fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      {custName}
                                    </Typography>
                                  </Box>
                                  {custEmail && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <Email fontSize="small" color="action" />
                                      <Typography variant="body2">
                                        {custEmail}
                                      </Typography>
                                    </Box>
                                  )}
                                  {custPhone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <Phone fontSize="small" color="action" />
                                      <Typography variant="body2">
                                        {custPhone}
                                      </Typography>
                                    </Box>
                                  )}
                                  {custWA && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <WhatsApp fontSize="small" color="action" />
                                      <Typography variant="body2">
                                        {custWA}
                                      </Typography>
                                    </Box>
                                  )}
                                </>
                              );
                            })()}
                          </Grid>

                          {/* Actions */}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => navigate(`/bookings/${booking.id}`)}
                              >
                                View Details
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default FrontDeskSearchPage;
